// ===================================================================
// 分镜模块类型定义
// 基于 iAPI: GET /proxy-aicomic/api/projects/episodes/storyboards
// 接口文档: https://iapi.baidu-int.com/web/project/367583/apis/api-4841885
// ===================================================================

import type {ResourceItem} from '@components/editor/utils/mention';
import type {ProjectId} from '@/types';

/**
 * 参考素材审核/生成状态（refImage / refVideo / audio 共用）
 *
 * 对应接口 status 字段：
 *   1=生成中  2=生成成功  3=生成失败
 *   4=风控审核中  5=风控通过  6=风控驳回  7=全部成功
 */
export const RefMaterialStatusValue = {
    /** 生成中 */
    Generating: 1,
    /** 生成成功 */
    Success: 2,
    /** 生成失败 */
    Failed: 3,
    /** 风控审核中 */
    Reviewing: 4,
    /** 风控通过 */
    Approved: 5,
    /** 风控驳回 */
    Rejected: 6,
    /** 全部成功 */
    AllSuccess: 7,
} as const;

export type RefMaterialStatus =
    (typeof RefMaterialStatusValue)[keyof typeof RefMaterialStatusValue];

/** 参考素材 UI 展示状态 */
export type RefMaterialUiStatus = 'empty' | 'generating' | 'generated' | 'failed';

/** 将接口状态映射为 UI 展示状态 */
export const REF_MATERIAL_STATUS_TO_UI: Record<RefMaterialStatus, RefMaterialUiStatus> = {
    [RefMaterialStatusValue.Generating]: 'generating',
    [RefMaterialStatusValue.Success]: 'generated',
    [RefMaterialStatusValue.Failed]: 'failed',
    [RefMaterialStatusValue.Reviewing]: 'generating',
    [RefMaterialStatusValue.Approved]: 'generated',
    [RefMaterialStatusValue.Rejected]: 'failed',
    [RefMaterialStatusValue.AllSuccess]: 'generated',
};

/**
 * 审核素材状态（imageMaterials / videoMaterials 共用）
 *
 * 对应接口 status 字段：0=待审核  1=通过  2=待定  3=淘汰
 */
export const AuditMaterialStatusValue = {
    /** 待审核（未标注任何状态，接口返回 0） */
    Unreviewed: 0,
    /** 通过 */
    Passed: 1,
    /** 待定 */
    Pending: 2,
    /** 淘汰 */
    Eliminated: 3,
} as const;

export type AuditMaterialStatus =
    (typeof AuditMaterialStatusValue)[keyof typeof AuditMaterialStatusValue];

/** 审核状态优先级：通过 > 待定 > 待审核 > 淘汰，既是缩略图展示顺序，也是整体预览的回退顺序 */
export const PREVIEW_STATUS_PRIORITY = [
    AuditMaterialStatusValue.Passed,
    AuditMaterialStatusValue.Pending,
    AuditMaterialStatusValue.Unreviewed,
    AuditMaterialStatusValue.Eliminated,
] as const;

/** 连播预览模式：在连播预览下拉里显式选择，不做本地记忆 */
export const PreviewModeValue = {
    /** 预览整体素材 */
    All: 0,
    /** 预览通过素材 */
    OnlyPassed: 1,
} as const;

export type PreviewMode = (typeof PreviewModeValue)[keyof typeof PreviewModeValue];

// ======================== 素材类型 ========================

/** 参考图素材（选中），对应接口 refImage 字段 */
export interface RefImageMaterial {
    /** 素材ID */
    materialId: number;
    /** 生成记录ID */
    genId: string;
    /** 图片地址 */
    url: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 生成状态：1=生成中 2=生成成功 3=生成失败 */
    genStatus: GenStatus;
    /** 标注状态 */
    remarkStatus: AuditMaterialStatus;
    /** 批注 */
    remark: string;
    /** 批注时间 */
    remarkTime: number;
    /** 上传人ID */
    uploadUserId: string;
    /** 上传人名称 */
    uploadUserName: string;
    /** 时长 */
    duration: string;
    /** 是否本人上传 */
    isOwner: boolean;
    /** 封面地址 */
    coverUrl: string;
}

/** 参考视频素材（选中），对应接口 refVideo 字段 */
export interface RefVideoMaterial {
    /** 素材ID */
    materialId: number;
    /** 生成记录ID */
    genId: string;
    /** 视频地址 */
    url: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 时长 */
    duration: string;
    /** 生成状态：1=生成中 2=生成成功 3=生成失败 */
    genStatus: GenStatus;
    /** 标注状态 */
    remarkStatus: AuditMaterialStatus;
    /** 批注 */
    remark: string;
    /** 批注时间 */
    remarkTime: number;
    /** 上传人ID */
    uploadUserId: string;
    /** 上传人名称 */
    uploadUserName: string;
    /** 是否本人上传 */
    isOwner: boolean;
    /** 封面地址 */
    coverUrl: string;
}

/** 音频素材（选中），对应接口 audio 字段 */
export interface AudioMaterial {
    /** 素材ID */
    materialId: number;
    /** 生成记录ID */
    genId: string;
    /** 音频地址 */
    url: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 时长 */
    duration: string;
    /** 生成状态：1=生成中 2=生成成功 3=生成失败 */
    genStatus: GenStatus;
    /** 标注状态： 1=通过 2=待定 3=淘汰 */
    remarkStatus: AuditMaterialStatus;
    /** 批注 */
    remark: string;
    /** 批注时间 */
    remarkTime: number;
    /** 上传人ID */
    uploadUserId: string;
    /** 上传人名称 */
    uploadUserName: string;
    /** 是否本人上传 */
    isOwner: boolean;
    /** 封面地址 */
    coverUrl: string;
}

/** 图片审核素材，对应接口 imageMaterials 列表项（分镜图素材列表-全部） */
export interface ImageMaterial {
    coverUrl: string;
    /** 素材ID */
    materialId: number;
    /** 生成记录ID */
    genId: string;
    /** 素材地址 */
    url: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 标注状态： 1=通过 2=备选 3=淘汰 */
    remarkStatus: AuditMaterialStatus;
    /** 生成状态：1=生成中 2=生成成功 3=生成失败 */
    genStatus: GenStatus;
    /** 批注 */
    remark: string;
    /** 批注时间 */
    remarkTime: number;
    /** 上传人ID */
    uploadUserId: string;
    /** 上传人名称 */
    uploadUserName: string;
    /** 是否本人上传 */
    isOwner: boolean;
    /** 时长 */
    duration: string;
}

/** 视频审核素材，对应接口 videoMaterials 列表项（分镜视频素材列表-全部） */
export interface VideoMaterial {
    /** 素材ID */
    materialId: number;
    /** 生成记录ID */
    genId: string;
    /** 素材地址 */
    url: string;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 时长 */
    duration: string;
    /** 标注状态：0=默认 1=首选 2=备选 3=淘汰 */
    remarkStatus: AuditMaterialStatus;
    /** 生成状态：1=生成中 2=生成成功 3=生成失败 */
    genStatus: GenStatus;
    /** 批注 */
    remark: string;
    /** 批注时间 */
    remarkTime: number;
    /** 上传人ID */
    uploadUserId: string;
    /** 上传人名称 */
    uploadUserName: string;
    /** 是否本人上传 */
    isOwner: boolean;
    /** 封面地址 */
    coverUrl: string;
}

// ======================== 分镜 ========================

/** 单条分镜数据 */
export interface Storyboard {
    /** 分镜id */
    id: number;
    /** 排序权重 */
    sortOrder: number;
    /** 分镜版本号 */
    version: number;
    /** 时长（秒） */
    duration: number;
    /** 景别 */
    sceneType: string;
    /** 画面描述 */
    sceneDesc: string;
    /** 角色动作 */
    characterAction: string;
    /** 光影氛围 */
    lightAtmosphere: string;
    /** 画面提示词 */
    imagePrompt: string;
    /** 视频提示词 */
    videoPrompt: string;
    /** 角色id，0=旁白 */
    characterId: number;
    /** 角色名称/旁白 */
    characterName: string;
    /** 台词 */
    dialogue: string;
    /** 声音/音效描述 */
    sound: string;
    /** 参考图素材（选中） */
    refImage: RefImageMaterial | null;
    /** 参考视频素材（选中） */
    refVideo: RefVideoMaterial | null;
    /** 音频素材（选中） */
    audio: AudioMaterial | null;
    /** 分镜图素材列表（全部） */
    imageMaterials: ImageMaterial[] | null;
    /** 分镜视频素材列表（全部） */
    videoMaterials: VideoMaterial[] | null;
    /** 图片创作器参考素材，创作时候垫图用 */
    imagePromptReferInfo: ResourceItem[] | null;
    /** 视频创作器参考素材，创作时候垫图用 */
    videoPromptReferInfo: ResourceItem[] | null;
}

/**
 * 素材生成状态
 * 1=生成中  2=生成成功  3=生成失败
 */
export const GenStatusValue = {
    Generating: 1,
    Success: 2,
    Failed: 3,
} as const;

export type GenStatus = (typeof GenStatusValue)[keyof typeof GenStatusValue];

/**
 * 素材总的生成状态
 * 1=生成中  2=生成结束
 */
export const MaterialGenStatusValue = {
    Generating: 1,
    Finished: 2,
} as const;

export type MaterialGenStatus =
    (typeof MaterialGenStatusValue)[keyof typeof MaterialGenStatusValue];

/**
 * 提示词生成状态
 * 1=生成中  2=生成成功  3=生成失败
 */
export const PromptGenStatusValue = {
    Generating: 1,
    Success: 2,
    Failed: 3,
} as const;

export type PromptGenStatus = (typeof PromptGenStatusValue)[keyof typeof PromptGenStatusValue];

/** 列显隐配置项 */
export interface ColumnConfigItem {
    /** 列字段 key */
    key: string;
    /** 是否可见 */
    visible: boolean;
    /** 列显示名称 */
    name: string;
    /** 排序序号 */
    order: number;
    /** 编辑态占位提示文本 */
    placeholder?: string;
    /** 输入框最大字符数 */
    maxLength?: number;
    /** 该列是否支持富文本编辑（按图生/参考生链路下发） */
    enableRichText?: boolean;
    /** 列标题 hover 气泡文案，不为空时展示 */
    tooltip?: string;
}

/** 列显隐配置 */
export interface ColumnsConfig {
    /** 各列显隐配置列表 */
    config: ColumnConfigItem[];
    /** 配置版本号 */
    version: number;
}

// ======================== API 响应 ========================

/** 美术设定画面比例 */
export type ComicRatio = '16:9' | '9:16' | '3:4' | '4:3';

/** 分镜列表响应 data */
export interface StoryboardListData {
    /** 项目 ID */
    projectId: ProjectId;
    /** 分集 ID */
    episodeId: number;
    /** 美术设定画面比例 */
    comicRatio: ComicRatio;
    /** 分镜数据列表 */
    storyboard: Storyboard[] | null;
    /** 列显隐配置 */
    columns: ColumnsConfig;
    /** 提示词生成状态：1=生成中 2=生成成功 3=生成失败 */
    promptGenStatus: PromptGenStatus;
    /** 素材总的生成状态：1=生成中 2=生成结束 */
    materialGenStatus: MaterialGenStatus;
    /** 项目名称 */
    projectName: string;
    /** 分集序号 */
    episodeNo: number;
    /** 视频生成链路类型：image2video=图生链路 refs2video=参考生链路 */
    videoModelType: VideoModelType;
}

/** 视频生成链路类型 */
export const VideoModelTypeValue = {
    /** 图生链路 */
    Image2Video: 'image2video',
    /** 参考生链路 */
    Refs2Video: 'refs2video',
} as const;

export type VideoModelType = (typeof VideoModelTypeValue)[keyof typeof VideoModelTypeValue];

// ======================== 修改分镜单元格 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4841983

/** 修改分镜单元格请求体 */
export interface UpdateStoryboardCellParams {
    /** 必填，要更新的分镜主键 */
    id: number;
    /** 必填，当前分镜版本号，防止并发冲突 */
    version: number;
    /** 必填，项目ID */
    projectId: string;
    /** 非0时覆盖 */
    duration?: number;
    /** 非空时覆盖 */
    sceneType?: string;
    /** 非空时覆盖 */
    sceneDesc?: string;
    /** 非空时覆盖 */
    characterAction?: string;
    /** 非空时覆盖 */
    lightAtmosphere?: string;
    /** 非空时覆盖 */
    imagePrompt?: string;
    /** 非空时覆盖 */
    videoPrompt?: string;
    /** 非空时覆盖 */
    dialogue?: string;
    /** 非空时覆盖 */
    sound?: string;
    /** 参考图 URL，非空时覆盖 */
    refImage?: string;
    /** 参考视频 URL，非空时覆盖 */
    refVideo?: string;
    /** 服务端做权限校验用 */
    episodeId?: number;
    /** 0表示"旁白"，非空时覆盖 */
    characterId?: number;
}

/** 修改分镜单元格响应 data */
export interface UpdateStoryboardCellData {
    storyboardId: number;
    version: number;
}

// ======================== 复制分镜 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4841962

/** 复制分镜请求体 */
export interface CopyStoryboardParams {
    /** 前一条分镜的 sortOrder */
    prevSortOrder: number;
    /** 后一条分镜的 sortOrder */
    nextSortOrder: number;
    /** 项目 ID */
    projectId: string;
    /** 源分镜 ID */
    sourceStoryboardId: number;
}

/** 复制分镜响应 data */
export interface CopyStoryboardData {
    /** 项目 ID */
    projectId: ProjectId;
    /** 分集 ID */
    episodeId: number;
    /** 新增的分镜 */
    storyboard: Storyboard;
}

// ======================== 删除分镜 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4841914

/** 删除分镜请求体 */
export interface DeleteStoryboardParams {
    /** 分镜 ID */
    storyboardId: number;
    /** 项目 ID */
    projectId: ProjectId;
}

// ======================== 分镜素材批注 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4842120

/** 素材类型 */
export const MaterialTypeValue = {
    /** 参考图 */
    REF_IMAGE: 'REF_IMAGE',
    /** 分镜图片 */
    IMAGE: 'IMAGE',
    /** 参考视频 */
    REF_VIDEO: 'REF_VIDEO',
    /** 分镜视频 */
    VIDEO: 'VIDEO',
    /** 音频 */
    AUDIO: 'AUDIO',
} as const;

export type MaterialType = (typeof MaterialTypeValue)[keyof typeof MaterialTypeValue];

/** 删除分镜资源请求体 */
export interface DeleteStoryboardMaterialParams {
    /** 素材类型 */
    materialType: MaterialType;
    /** 当前分镜版本号 */
    version: number;
    /** 分镜 ID */
    storyboardId: number;
    /** 项目 ID */
    projectId: string;
    /** 素材 ID */
    materialId: number;
}

/** 删除分镜资源响应 data */
export interface DeleteStoryboardMaterialData {
    storyboardId: number;
    materialId: number;
    materialType: MaterialType;
    version: number;
}

/** 分镜素材批注请求体 */
export interface RemarkMaterialParams {
    /** 分镜 ID */
    storyboardId: number;
    /** 素材 ID */
    materialId: number;
    /** 审核状态：1=通过 2=待定 3=淘汰 */
    status?: AuditMaterialStatus;
    /** 批注内容 */
    remark?: string;
    /** 当前分镜版本号（乐观锁） */
    storyboardVersion: number;
    /** 操作人 UID（服务端可从登录态获取） */
    operatorUid?: number;
    /** 素材类型 */
    materialType: MaterialType;
    /** 项目 ID */
    projectId: string;
}

/** 分镜素材批注响应 data */
export interface RemarkMaterialData {
    /** 分镜 ID */
    storyboardId: number;
    /** 操作成功后分镜版本号+1 */
    version: number;
}

// ======================== 提示词生成重试 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4868952

/** 提示词生成重试请求体 */
export interface RetryPromptGenParams {
    /** 剧集 ID */
    episodeId: number;
    /** 项目 ID */
    projectId: string;
}

/** 提示词生成重试响应 data */
export interface RetryPromptGenData {
    /** 剧集 ID */
    episodeId: number;
    /** 任务 ID */
    taskId: number;
}

// ======================== 分镜拖拽重排序 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4841915

/** 分镜拖拽重排序请求体 */
export interface ReorderStoryboardParams {
    /** 被拖拽的分镜 ID */
    storyboardId: number;
    /** 目标位置前一条分镜的 sortOrder */
    prevSortOrder: number;
    /** 目标位置后一条分镜的 sortOrder */
    nextSortOrder: number;
    /** 项目 ID */
    projectId: string;
}

/** 分镜拖拽重排序响应 data */
export interface ReorderStoryboardData {
    /** 分镜 ID */
    storyboardId: number;
    /** 新的排序权重 */
    sortOrder: number;
    /** 新的版本号 */
    version: number;
}

// ======================== 新增空分镜 ========================
// https://iapi.baidu-int.com/web/project/367583/apis/api-4841913

/** 新增空分镜请求体 */
export interface CreateStoryboardParams {
    /** 前一条分镜的 sortOrder */
    prevSortOrder: number;
    /** 后一条分镜的 sortOrder */
    nextSortOrder: number;
    /** 项目 ID */
    projectId: string;
    /** 剧集 ID */
    episodeId: number;
}

// ======================== 单个分镜信息 ========================
// POST /proxy-aicomic/storyboard/info

/** 单个分镜信息请求体 */
export interface StoryboardInfoParams {
    /** 项目 ID */
    projectId: string;
    /** 分镜 ID */
    storyboardId: number;
}

/** 单个分镜信息响应 data */
export interface StoryboardInfoData {
    /** 分镜id */
    storyboardId?: number;
    /** 项目id */
    projectId?: string;
    /** 分镜序号 */
    storyboardNo?: number;
    /** 画面提示词 */
    imagePrompt?: string;
    /** 创建时间 */
    createTime?: string;
    /** 更新时间 */
    updateTime?: string;
    /** 视频提示词 */
    videoPrompt?: string;
    /** 分镜图生成参考图 */
    imagePromptReferInfo: ResourceItem[] | null;
    /** 分镜视频生成参考图 */
    videoPromptReferInfo: ResourceItem[] | null;
    /** 分镜时长（秒） */
    duration: string;
}

// ======================== 列设置保存 ========================

/** 单列设置项（当前仅支持 visible，后续可扩展） */
export interface ColumnSettingItem {
    /** 列字段 key */
    key: string;
    /** 是否可见 */
    visible: boolean;
}

/** 保存列展示设置请求参数 */
export interface SaveColumnSettingsParams {
    /** 项目 ID */
    projectId: string;
    /** 分集 ID（comic_episodes.id） */
    episodeId: number;
    /** 列设置列表（全量） */
    columns: ColumnSettingItem[];
}
